import {
  formatNumber,
  PresetNumberFormatSpecifier,
} from '@vertex-protocol/react-client';
import type { BigDecimal } from '@vertex-protocol/client';
import { ErrorPanel } from 'client/components/ErrorPanel';
import { TriggerOrderEnableOneClickTradingInfo } from 'client/modules/trading/components/TriggerOrderEnableOneClickTradingInfo';
import { OrderFormError } from 'client/modules/trading/types';

interface Props {
  formError: OrderFormError | undefined;
  baseSymbol?: string;
  quoteSymbol?: string;
  minAssetOrderSize?: BigDecimal;
  executionConversionPrice?: BigDecimal;
}

export function TradingErrorPanel({
  formError,
  baseSymbol,
  quoteSymbol,
  minAssetOrderSize,
  executionConversionPrice,
}: Props) {
  const errorContent = (() => {
    switch (formError) {
      case 'below_min':
        return (
          <MinimumOrderSizeError
            baseSymbol={baseSymbol}
            quoteSymbol={quoteSymbol}
            minAssetOrderSize={minAssetOrderSize}
            executionConversionPrice={executionConversionPrice}
          />
        );
      case 'trigger_order_single_signature_disabled':
        return <TriggerOrderEnableOneClickTradingInfo />;
      case 'zero_buying_power_only_fok':
        return 'You have no buying power. Deposit more collateral or close open positions via market orders.';
      default:
        return null;
    }
  })();

  if (!errorContent) {
    return null;
  }

  return <ErrorPanel>{errorContent}</ErrorPanel>;
}

function MinimumOrderSizeError({
  baseSymbol,
  quoteSymbol,
  minAssetOrderSize,
  executionConversionPrice,
}: {
  baseSymbol?: string;
  quoteSymbol?: string;
  minAssetOrderSize?: BigDecimal;
  executionConversionPrice?: BigDecimal;
}) {
  if (!minAssetOrderSize) {
    return 'This order is below the minimum trade size. Increase Amount or Total to continue.';
  }

  const minimumQuoteAmount = executionConversionPrice
    ? minAssetOrderSize.multipliedBy(executionConversionPrice)
    : undefined;

  return (
    <span>
      Minimum order size is{' '}
      <span className="text-text-primary">
        {formatNumber(minAssetOrderSize, {
          formatSpecifier: PresetNumberFormatSpecifier.NUMBER_UPTO_6DP,
        })}
        {baseSymbol ? ` ${baseSymbol}` : ''}
      </span>
      {minimumQuoteAmount && (
        <>
          {' '}
          (about{' '}
          <span className="text-text-primary">
            {formatNumber(minimumQuoteAmount, {
              formatSpecifier: PresetNumberFormatSpecifier.NUMBER_2DP,
            })}
            {quoteSymbol ? ` ${quoteSymbol}` : ''}
          </span>
          )
        </>
      )}
      . Increase Amount or Total to continue.
    </span>
  );
}
